import { useState } from 'react'
import { router, Link, usePage } from '@inertiajs/react'
import { useLocale } from '../lib/LocaleContext'

const TYPE_DOT_COLORS = {
  architecture:    '#34d399',
  decision:        '#a78bfa',
  bugfix:          '#f87171',
  discovery:       '#60a5fa',
  pattern:         '#fb923c',
  config:          '#facc15',
  preference:      '#e879f9',
  session_summary: '#94a3b8',
  reference:       '#2dd4bf',
  chore:           '#71717a',
}

export default function Sidebar({ projects, sessions, workspaces, active_project, active_session, onNavigate, open = true }) {
  const { auth } = usePage().props
  const isOwner = auth?.user?.role === 'owner'
  const { locale, t } = useLocale()

  const [projectsOpen, setProjectsOpen] = useState(true)
  const [sessionsOpen, setSessionsOpen] = useState(true)
  const [filter, setFilter] = useState('')

  const totalCount = projects.reduce((sum, p) => sum + p.memory_count, 0)

  const filteredProjects = filter
    ? projects.filter(p => p.name.toLowerCase().includes(filter.toLowerCase()))
    : projects

  const filteredSessions = filter
    ? sessions.filter(s => (s.project_name || '').toLowerCase().includes(filter.toLowerCase()))
    : sessions

  function navigate(slug) {
    router.visit("/", { data: slug ? { project: slug } : {}, preserveScroll: false })
    onNavigate?.()
  }

  function navigateSession(id) {
    router.visit("/", { data: { session: id }, preserveScroll: false })
    onNavigate?.()
  }

  return (
    <aside className={[
      "shrink-0 border-r border-zinc-700 overflow-x-hidden transition-all duration-200 flex flex-col",
      open ? "w-72" : "w-0 border-r-0 overflow-hidden",
    ].join(" ")}>
      <div className="px-3 py-4 space-y-0.5 w-full">
        {workspaces && workspaces.length > 1 && (
          <div className="mb-3 pb-3 border-b border-zinc-700">
            <p className="px-2 text-[10px] font-mono text-zinc-400 uppercase tracking-[0.18em] mb-1 flex items-center gap-2">
              <span className="w-4 h-px bg-zinc-500 inline-block shrink-0" />
              {t('nav.workspace')}
            </p>
            {workspaces.map(w => (
              <button
                key={w.id}
                onClick={() => !w.active && router.post('/workspace/switch', { workspace_id: w.id })}
                className={[
                  "w-full text-left px-2 py-1 rounded text-xs font-mono transition-colors",
                  w.active ? "text-emerald-400 font-semibold cursor-default" : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800"
                ].join(" ")}
              >
                {w.active ? "● " : "○ "}{w.name}
              </button>
            ))}
          </div>
        )}
        <SidebarItem
          label={t('nav.all')}
          count={totalCount}
          active={!active_project && !active_session}
          onClick={() => navigate(null)}
        />
        <div className="px-3 py-2 relative">
          <input
            type="text"
            placeholder={t('nav.filter')}
            value={filter}
            onChange={e => setFilter(e.target.value)}
            className="w-full bg-zinc-800/60 border border-zinc-700 rounded-lg px-2 py-1 text-xs font-mono text-zinc-300 placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-emerald-500/30 focus:border-emerald-700 transition-colors"
          />
          {filter && (
            <button
              onClick={() => setFilter('')}
              className="absolute right-5 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 text-xs"
            >×</button>
          )}
        </div>
        {filteredProjects.length > 0 && (
        <div className="pt-2 pb-1">
          <button
            onClick={() => setProjectsOpen(o => !o)}
            className="flex items-center justify-between w-full px-2 py-1 text-[10px] font-mono font-semibold tracking-[0.18em] text-zinc-400 hover:text-zinc-200 uppercase transition-colors"
          >
            <span className="flex items-center gap-2">
              <span className="w-4 h-px bg-zinc-500 inline-block shrink-0" />
              {t('nav.projects')}
            </span>
            <span className={`transition-transform duration-200 ${projectsOpen ? 'rotate-0' : '-rotate-90'}`}>▾</span>
          </button>
        </div>
        )}
        {filteredProjects.length > 0 && projectsOpen && filteredProjects.map(p => (
          <SidebarItem
            key={p.slug}
            label={p.name}
            count={p.memory_count}
            active={active_project === p.slug}
            onClick={() => navigate(p.slug)}
            type_counts={p.type_counts}
          />
        ))}
        {filteredSessions.length > 0 && (
          <>
            <div className="pt-4 pb-1 mt-2 border-t border-zinc-700">
              <button
                onClick={() => setSessionsOpen(o => !o)}
                className="flex items-center justify-between w-full px-2 py-1 text-[10px] font-mono font-semibold tracking-[0.18em] text-zinc-400 hover:text-zinc-200 uppercase transition-colors"
              >
                <span className="flex items-center gap-2">
                  <span className="w-4 h-px bg-zinc-500 inline-block shrink-0" />
                  {t('nav.sessions')}
                </span>
                <span className={`transition-transform duration-200 ${sessionsOpen ? 'rotate-0' : '-rotate-90'}`}>▾</span>
              </button>
            </div>
            {sessionsOpen && filteredSessions.map(s => (
              <SessionItem
                key={s.id}
                session={s}
                active={active_session?.id === s.id}
                onClick={() => navigateSession(s.id)}
              />
            ))}
            {sessionsOpen && sessions.length === 5 && !filter && (
              <p className="px-3 py-1 text-[10px] text-zinc-400 font-mono">{t('nav.last5')}</p>
            )}
          </>
        )}
        <div className="pt-3 border-t border-zinc-700 mt-2 space-y-0.5">
          <a
            href={`/docs/${locale}`}
            className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs font-mono text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800/60 transition-colors"
          >
            <span className="w-3 h-px bg-zinc-500 inline-block shrink-0" />
            {t('nav.docs')}
          </a>
          <Link
            href="/settings/profile/edit"
            className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs font-mono text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800/60 transition-colors"
          >
            <span className="w-3 h-px bg-zinc-500 inline-block shrink-0" />
            {t('nav.profile')}
          </Link>
          {isOwner && (
            <Link
              href="/settings/users"
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs font-mono text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800/60 transition-colors"
            >
              <span className="w-3 h-px bg-zinc-500 inline-block shrink-0" />
              {t('nav.settings')}
            </Link>
          )}
        </div>
      </div>
    </aside>
  )
}

function SidebarItem({ label, count, active, onClick, type_counts }) {
  const titleTooltip = type_counts?.length > 0
    ? type_counts.map(tc => `${tc.type}: ${tc.count}`).join(' · ')
    : undefined

  return (
    <button
      onClick={onClick}
      title={titleTooltip}
      className={[
        "group w-full flex items-center justify-between px-2 py-1 rounded text-sm font-mono transition-colors",
        active
          ? "bg-emerald-900/30 text-emerald-300 border-l-2 border-emerald-500/70 pl-[6px]"
          : "text-zinc-300 hover:text-zinc-100 hover:bg-zinc-800 border-l-2 border-transparent pl-[6px]",
      ].join(" ")}
    >
      <span className="truncate">{label}</span>
      <span className="flex items-center gap-1 shrink-0 ml-2">
        {type_counts?.length > 0 && (
          <span className="hidden group-hover:flex items-center gap-0.5">
            {type_counts.slice(0, 3).map(tc => (
              <span
                key={tc.type}
                className={`w-1.5 h-1.5 rounded-full dot-type-${tc.type in TYPE_DOT_COLORS ? tc.type : 'default'}`}
                title={`${tc.type}: ${tc.count}`}
              />
            ))}
          </span>
        )}
        <span className="text-xs text-zinc-400">{count}</span>
      </span>
    </button>
  )
}

function SessionItem({ session, active, onClick }) {
  const label = session.project_name || 'unknown'
  const date = new Date(session.started_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  const isOpen = !session.ended_at

  return (
    <button
      onClick={onClick}
      className={[
        "w-full flex items-start justify-between px-2 py-1 rounded text-xs font-mono transition-colors gap-2",
        active
          ? "bg-violet-900/40 text-violet-300"
          : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800",
      ].join(" ")}
    >
      <span className="truncate flex-1 text-left">{label}</span>
      <span className="shrink-0 flex items-center gap-1">
        {isOpen && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400/70 inline-block" />}
        <span className="text-zinc-400">{date}</span>
      </span>
    </button>
  )
}
